import React, {useEffect, useState} from 'react'
import {Box, ListSubheader, MenuItem, TextField} from '@mui/material'
import {isSpecialBin} from '../../utils'

interface BinSelectorProps {
    binRow?: string
    onBinSelect: (binId: string) => void
    allBins: string[]
}

const BinSelector = ({binRow, onBinSelect, allBins}: BinSelectorProps) => {
    const [selectedRow, setSelectedRow] = useState<string>(binRow || '')
    const [selectedSleeveBin, setSelectedSleeveBin] = useState<string>('')
    const [sleeveBinOptions, setSleeveBinOptions] = useState<React.JSX.Element[]>([])

    const handleRowChange = (e: {target: {value: string}}) => {
        setSelectedRow(e.target.value)
        setSelectedSleeveBin('')
        if (isSpecialBin(e.target.value)) {
            onBinSelect(e.target.value)
        }
    }

    const handleSleeveBinChange = (e: {target: {value: string}}) => {
        let selectedBin = e.target.value
        let currentBinName = selectedBin.startsWith('01') || isSpecialBin(selectedBin) ? selectedBin : `01 ${selectedBin}`
       setSelectedSleeveBin(selectedBin)
        onBinSelect(currentBinName)
    }

    let {rows, sleeveRows} = (() => {
        let rows: string[] = []
        let sleeveRows: Record<
            string,
            {sleevePart: string; sleeveBin: string}[]
        > = {}

        allBins.forEach(bin => {
            let [rowNum, rowLetter, sleeveBin] = bin.split(' ')
            if (!rows.includes(rowLetter)) {
                rows.push(rowLetter)
                sleeveRows[rowLetter] = []
            }
            let sleevePart = sleeveBin?.slice(0, 2) || sleeveBin
            sleeveRows[rowLetter].push({sleevePart, sleeveBin})
        })

        return {rows, sleeveRows}
    })()

    useEffect(() => {
        if (!selectedRow || !sleeveRows[selectedRow]) {
            setSleeveBinOptions([])
            return
        }

        let groupedBySleeve: Record<string, string[]> = {}

        sleeveRows[selectedRow].forEach(({sleevePart, sleeveBin}) => {
            if (!groupedBySleeve[sleevePart]) {
                groupedBySleeve[sleevePart] = []
            }
            groupedBySleeve[sleevePart].push(sleeveBin)
        })

        let options: React.JSX.Element[] = []
        let sleeveParts = Object.keys(groupedBySleeve).sort()

        for (let sleevePart of sleeveParts) {
            options.push(
                <ListSubheader key={`header-${sleevePart}`}>
                    {`Sleeve-${sleevePart}`}
                </ListSubheader>
            )

            let sortedBins = groupedBySleeve[sleevePart].sort()

            for (let sleeveBin of sortedBins) {
                options.push(
                    <MenuItem
                        key={`item-${sleevePart}-${sleeveBin}`}
                        value={`${selectedRow} ${sleeveBin}`}>
                        {sleeveBin}
                    </MenuItem>
                )
            }
        }

        setSleeveBinOptions(options)
    }, [selectedRow, sleeveRows])

    return (
        <Box>
            <TextField
                data-testid="bin-selector"
                label="Select Row"
                select
                variant="outlined"
                size="small"
                value={selectedRow}
                onChange={handleRowChange}
                sx={{width: 200, marginBottom: 2}}>
                {rows.map((row, index) => (
                    <MenuItem key={`row-${row}-${index}`} value={row}>
                        {row}
                    </MenuItem>
                ))}
                <MenuItem key="drawer" value="DRAWER">
                    DRAWER
                </MenuItem>
                <MenuItem key="overstock" value="OVERSTOCK">
                    OVERSTOCK
                </MenuItem>
            </TextField>
            <TextField
                label="Select Sleeve & Bin"
                select
                variant="outlined"
                size="small"
                value={selectedSleeveBin}
                onChange={handleSleeveBinChange}
                sx={{width: 200}}
                disabled={
                    !selectedRow ||
                    selectedRow === 'DRAWER' ||
                    selectedRow === 'OVERSTOCK'
                }>
                {sleeveBinOptions}
            </TextField>
        </Box>
    )
}

export default BinSelector
